import React from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Marquee from "@/components/Marquee";
import EventName from "@/components/EventName";
import Brand from "@/components/Brand";
import HeroPromoCard from "@/components/HeroPromoCard";
import { IMAGES, LOGO_URL_TIGHT } from "@/lib/brand";
import { CARS, MOTORCYCLES } from "@/data/builds";

// Marquee items pulled from the canonical builds knowledge base.
// Each item is { slug, name } so the marquee can link to its detail page.
const FEATURED_BUILDS = CARS.map((b) => ({ slug: b.slug, name: b.name }));
const FEATURED_BIKES = MOTORCYCLES.map((b) => ({ slug: b.slug, name: b.name }));

export default function Landing() {
    return (
        <div className="min-h-screen bg-[var(--crewz-bg)] text-[var(--crewz-text)]" data-testid="landing-page">
            <Navbar />

            {/* HERO — APPROVED AND LOCKED (revised composition, July 2026).
                Baseline for the homepage. Not to be reopened unless an
                objective usability issue, responsive defect, or technical
                implementation problem is identified, or the owner
                explicitly requests a new review.

                Locked composition — Lower-centred (Option B, refined):
                  • Hero image: IMAGES.hero (unchanged), ken-burns, object-cover
                  • Logo asset: LOGO_URL_TIGHT ("/logo-crewznation-tight.png?v=1")
                    — Hero-only tight-cropped derivative of the interim
                    wordmark; Navbar and Footer continue to use the global
                    LOGO_URL. The tight crop is the visible artwork bounds
                    (984 × 400) of the original PNG (1385 × 1045) — 30.8%
                    transparent bottom canvas removed to eliminate phantom
                    spacing between the logo and the descriptor.
                  • Section: flex items-end (cluster anchored to bottom)
                  • Content wrapper padding: pb-14 md:pb-12 lg:pb-14
                  • Logo container: mx-auto (horizontally centred)
                  • Logo H1 max-width: max-w-[12rem] sm:max-w-[13rem]
                    md:max-w-[14.5rem] lg:max-w-[17rem] xl:max-w-[19rem]
                    (approx. +17% vs. prior sizing — owner-authorized
                    Feb 2026 for stronger institutional presence)
                  • Supporting statement color: text-[var(--crewz-text)]/90
                    (white, restored Feb 2026). Contrast solved on the
                    image side via a subtle deepening of the existing
                    bottom-field gradient — no text-shadow, no filter,
                    no localized overlay behind the text.
                  • Descriptor: unchanged wording; mt-4 md:mt-6 spacing;
                    max-w-xl md:max-w-none
                  • Continuation cue: unchanged (absolute bottom-6)

                Measured outcomes at approval:
                  • Horizontal centre of logo: 50.0% desktop and mobile
                  • Logo TOP position: 75.5% desktop / 67.6% mobile
                  • Logo BOTTOM position: 86.1% desktop / 76.3% mobile
                  • Visible gap logo → descriptor: 24px desktop / 16px mobile
                  • Descriptor → cue gap: +4px both breakpoints
                  • No overlap between the logo and any vehicle

                Locked elements: image, wordmark asset choice, wordmark
                scale/position/max-widths, descriptor wording/width,
                wordmark-to-descriptor spacing, section bottom padding,
                background gradient treatment, continuation cue placement,
                desktop and mobile responsive composition.

                See PRD §2.1.1. */}
            <section className="relative min-h-[calc(100vh-6rem)] flex items-end overflow-hidden border-b border-white/5">
                {/* Structural anchor container (Feb 2026): shares the same
                    max-w-[62rem] and mx-auto centering as the crest
                    cluster below, so the promo card's horizontal
                    position is anchored to the HERO CONTENT box — not
                    to the viewport edge. On wide viewports the box
                    stays centred and the promo tracks with it,
                    producing a visually integrated placement inset
                    naturally from the viewport corner. */}
                <div className="absolute inset-0 pointer-events-none z-10">
                    <div className="relative mx-auto h-full w-full max-w-[62rem] px-4 md:px-0">
                        <HeroPromoCard locale="en" />
                    </div>
                </div>
                <div className="absolute inset-0 overflow-hidden">
                    <picture>
                        {/* Mobile-optimized variant (Feb 2026 iOS
                            decode fix): the original 6240×3512 hero
                            image (~15 MB) exceeds iOS Safari's
                            single-<img> decode budget and rendered as
                            a broken-image icon on real iPhones.
                            Serve a 1440×810 compressed variant to
                            <md viewports; desktop keeps the original
                            asset and framing untouched. */}
                        <source
                            media="(max-width: 767px)"
                            srcSet="/hero-mobile.jpg"
                        />
                        <img
                            src={IMAGES.hero}
                            alt="CrewZNatioN"
                            width={6240}
                            height={3512}
                            loading="eager"
                            decoding="async"
                            fetchpriority="high"
                            className="absolute top-0 h-full object-cover left-0 md:left-[-28%] w-full md:w-[134%]"
                            style={{ maxWidth: "none" }}
                        />
                    </picture>
                    <div className="absolute inset-0 bg-gradient-to-t from-[var(--crewz-bg)]/95 via-[rgba(17,17,17,0.25)] to-[rgba(17,17,17,0.1)]" />
                    <div className="absolute inset-0 bg-gradient-to-l from-[rgba(17,17,17,0.6)] via-transparent to-transparent" />

                    {/* Broad, soft lower-field darkening — provides typographic
                        separation from the paving without a perceptible edge or
                        localized shape. Spans full width and fades gently upward.
                        Slightly deepened Feb 2026 to preserve white tagline
                        legibility (owner-authorized, supporting-statement colour
                        remains white). */}
                    <div
                        className="absolute inset-0 pointer-events-none"
                        aria-hidden="true"
                        style={{
                            background:
                                "linear-gradient(to top, rgba(0,0,0,0.58) 0%, rgba(0,0,0,0.4) 30%, rgba(0,0,0,0.2) 55%, rgba(0,0,0,0.06) 75%, transparent 90%)",
                        }}
                    />
                </div>

                <div className="relative w-full px-6 md:px-12 lg:px-20 pb-1 md:pb-1">
                    <div
                        className="mx-auto max-w-2xl md:max-w-[62rem] flex flex-col items-center text-center"
                    >
                        <h1
                            className="w-full max-w-[7rem] sm:max-w-[7.75rem] md:max-w-[8.75rem] lg:max-w-[10rem] xl:max-w-[11rem] mx-auto hero-wordmark"
                            data-testid="hero-title"
                        >
                            <img
                                src={LOGO_URL_TIGHT}
                                alt="CrewZNatioN"
                                className="w-full h-auto block select-none"
                                draggable="false"
                            />
                        </h1>
                        <p className="font-body text-[16px] md:text-[22px] font-normal text-[var(--crewz-text)]/90 max-w-xl md:max-w-none mt-3 md:mt-4 px-5 md:px-0 leading-[1.4] md:leading-[1.35] hero-tagline" data-testid="hero-tagline">
                            A professionally governed automotive and motorcycle community rooted in Riyadh, bringing together exceptional machines and the people who care for them.
                        </p>
                        <p
                            className="font-display text-xl md:text-2xl lg:text-3xl text-[var(--crewz-text)]/90 leading-none whitespace-nowrap mt-3 md:mt-4"
                            data-testid="hero-smiles-per-liter"
                        >
                            Smiles Per Liter
                        </p>
                        <a
                            href="#institutional-presence"
                            aria-label="Continue"
                            className="mt-1 md:mt-2 text-[var(--crewz-gold)]"
                            data-testid="hero-continuation-cue"
                        >
                            <ChevronDown size={20} strokeWidth={2.25} />
                        </a>
                    </div>
                </div>

                {/* Continuation cue lives inside the hero group above,
                    directly beneath SMILES PER LITER on the pavement. */}
            </section>

            {/* SECTION 1 — INSTITUTIONAL PRESENCE */}
            <section
                id="institutional-presence"
                className="bg-[var(--crewz-bg)] border-b border-white/5"
                data-testid="section-institutional-presence"
            >
                <div className="px-6 md:px-12 lg:px-20 py-28 md:py-40 max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
                    <div className="md:col-span-4">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="gold-rule" />
                            <p className="overline overline-primary">The Institution</p>
                        </div>
                    </div>
                    <div className="md:col-span-8">
                        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.01em] text-[var(--crewz-text)]">
                            Rooted in Riyadh. Connected to the World.
                        </h2>
                        <p className="font-body text-base md:text-lg text-[var(--crewz-text)]/85 leading-[1.85] mt-8 max-w-2xl">
                            <Brand /> is rooted in Riyadh, Saudi Arabia, with a regional presence and a global outlook. It exists to preserve, develop, and connect the culture surrounding exceptional machines and the people who care for them.
                        </p>
                    </div>
                </div>
            </section>

            {/* SECTION 2 — THE COMMUNITY AND ITS CHARACTER
                Imagery slot preserved for authentic community photography (people alongside machines).
                Not filled with machine-centric substitute imagery per approved direction. */}
            <section
                className="section-light border-y border-black/5"
                data-testid="section-community"
            >
                <div className="px-6 md:px-12 lg:px-20 py-28 md:py-40 max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
                    <div className="md:col-span-4">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="gold-rule" />
                            <p className="overline overline-primary">The Community</p>
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl leading-[1.02] tracking-[-0.01em] text-[var(--crewz-text-dark)]">
                            Built Around People. Held Together by Standards.
                        </h2>
                    </div>
                    <div className="md:col-span-8 space-y-6">
                        <p className="font-body text-base md:text-lg text-[var(--crewz-text-dark)]/85 leading-[1.85] max-w-2xl">
                            <Brand /> is built around people who share a commitment to exceptional machines and the culture surrounding them. The machines are the occasion. The people are the reason.
                        </p>
                        <p className="font-body text-base md:text-lg text-[var(--crewz-text-dark)]/85 leading-[1.85] max-w-2xl">
                            It is guided by responsible enthusiasm — passion accompanied by responsibility, shared knowledge, mutual respect, and stewardship.
                        </p>

                        {/* Implementation capacity for authentic community imagery
                            (people alongside machines) is preserved within this section.
                            No visible placeholder is exposed on the public surface.
                            When authentic photography is supplied, it will be inserted
                            here as a complete composition. */}
                    </div>
                </div>
            </section>

            {/* SECTION 3 — INSTITUTIONAL ACTIVITY
                Includes the reframed evidence block and the marquees, framed as the machines
                of our community rather than as an autonomous catalog. */}
            <section
                className="bg-[var(--crewz-bg)]"
                data-testid="section-activity"
            >
                <div className="px-6 md:px-12 lg:px-20 pt-28 md:pt-40 pb-16 max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
                    <div className="md:col-span-4">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="gold-rule" />
                            <p className="overline overline-primary">The Community in Motion</p>
                        </div>
                    </div>
                    <div className="md:col-span-8">
                        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.01em] text-[var(--crewz-text)]">
                            From the Road to the Gathering.
                        </h2>
                        <p className="font-body text-base md:text-lg text-[var(--crewz-text)]/85 leading-[1.85] mt-8 max-w-2xl">
                            CrewZNatioN comes to life through the people it brings together — in the journeys, the meets, and the gatherings.
                        </p>
                    </div>
                </div>

                {/* Marquee band — framed as the machines of the community */}
                <div className="mt-12">
                    <div className="px-6 md:px-12 lg:px-20 max-w-6xl mx-auto">
                        <p className="overline text-[var(--crewz-text)]/55 mb-6">
                            Our Cars. Our Bikes. Our Community.
                        </p>
                    </div>
                    <div className="bg-[var(--crewz-bg-2)] border-y border-white/5" data-testid="featured-marquee">
                        <Marquee items={FEATURED_BUILDS} speed={38} testid="featured-cars-marquee" />
                        <Marquee items={FEATURED_BIKES} speed={42} reverse testid="featured-bikes-marquee" />
                    </div>
                </div>

                {/* Evidence block — reframed as institutional convening evidence,
                    with truthful provenance to the first edition of The Gathering. */}
                <div className="px-6 md:px-12 lg:px-20 pt-20 pb-28 md:pb-32 max-w-6xl mx-auto">
                    <div className="flex items-center gap-3 mb-10">
                        <span className="gold-rule" />
                        <p className="overline">Bringing the Community Together.</p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10">
                        <div className="text-center" data-testid="stat-participating-vehicles">
                            <div className="font-display text-4xl md:text-5xl text-[var(--crewz-text)]">1,100+</div>
                            <p className="mt-3 text-xs md:text-sm text-[var(--crewz-text)]/70 leading-relaxed">
                                Participating Vehicles and Motorcycles
                            </p>
                        </div>
                        <div className="text-center" data-testid="stat-visitors">
                            <div className="font-display text-4xl md:text-5xl text-[var(--crewz-text)]">17,000+</div>
                            <p className="mt-3 text-xs md:text-sm text-[var(--crewz-text)]/70 leading-relaxed">
                                Visitors in Approximately Eight+ Hours
                            </p>
                        </div>
                        <div className="text-center" data-testid="stat-home">
                            <div className="font-display text-3xl md:text-4xl text-[var(--crewz-text)]">Riyadh</div>
                            <p className="mt-3 text-xs md:text-sm text-[var(--crewz-text)]/70 leading-relaxed">
                                Our Home
                            </p>
                        </div>
                        <div className="text-center" data-testid="stat-horizon">
                            <div className="font-display text-2xl md:text-3xl text-[var(--crewz-text)] leading-tight">
                                Regional and International
                            </div>
                            <p className="mt-3 text-xs md:text-sm text-[var(--crewz-text)]/70 leading-relaxed">
                                Our Reach
                            </p>
                        </div>
                    </div>
                    <p className="mt-10 text-xs md:text-sm tracking-[0.14em] uppercase text-[var(--crewz-text)]/60 text-center font-medium">
                        Figures refer to the first edition of <EventName />.
                    </p>
                </div>
            </section>

            {/* SECTION 4 — THE GATHERING AS FLAGSHIP EXPRESSION
                Structurally continuous with Section 3. Prominent but subordinate. */}
            <section
                className="bg-[var(--crewz-bg-2)] border-t border-white/5"
                data-testid="section-gathering"
            >
                <div className="px-6 md:px-12 lg:px-20 py-28 md:py-40 max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
                    <div className="md:col-span-4 md:-translate-x-8 lg:-translate-x-10" data-testid="flagship-title-group">
                        <div className="flex items-center gap-3 mb-6 md:translate-y-1" data-testid="flagship-overline">
                            <span className="gold-rule" />
                            <p className="overline overline-primary">The Flagship Expression</p>
                        </div>
                        <h2 className="text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.015em] text-[var(--crewz-text)]" style={{ fontFamily: '"Alexandria", "Inter", system-ui, sans-serif', fontWeight: 700 }}>
                            <EventName variant="stacked" />
                        </h2>
                    </div>
                    <div className="md:col-span-8">
                        <p className="font-body text-base md:text-lg text-[var(--crewz-text)]/85 leading-[1.85] max-w-2xl">
                            <EventName /> is the most public of <Brand />&rsquo;s activities — bringing exceptional machines, their owners, and the wider community together in Riyadh. It is one expression of <Brand />, not the whole of it.
                        </p>

                        <Link
                            to="/gathering"
                            className="group mt-10 inline-flex items-baseline gap-3 font-display text-xl md:text-2xl text-[var(--crewz-text)]/90 tracking-[0.01em] hover:text-[var(--crewz-gold)] transition-colors duration-300"
                            data-testid="gathering-cta"
                        >
                            <span className="border-b border-transparent group-hover:border-[var(--crewz-gold)]/60 transition-colors duration-300 pb-0.5">
                                1&ndash;3 October 2026 <span className="text-[var(--crewz-text)]/40 group-hover:text-[var(--crewz-gold)]/50 transition-colors duration-300">·</span> Rimal Center, Riyadh
                            </span>
                            <ArrowRight size={18} strokeWidth={1.5} className="translate-y-0.5 group-hover:translate-x-1 transition-transform duration-300" />
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
